import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { fromJS } from "immutable";
import { updateTimelineTime, updateProjectData } from "../../../../redux/actions/timelineUtils";
import { buildLocalSmartfocus } from "../../../timeline/timeline-helper";
import { ScaleHeader, SearchInput, SearchInputContainer, SfScaleContainer } from "./smartfocus-components";
import vmTheme from "../../../../constants/theme";
import Slider from "../../../zoom/zoom-slider";
import Typography from "../../../../common-components/Typography";
import { font } from "../../../../constants/font";
import { setWorkspaceTextFocus } from "../../../../redux/actions/appUtils";
import useMobileDetection from "../../../../helper/hooks/useMobileDetection";

const SmartfocusScale = ({ handleScaleValue, isLock }) => {
    const dispatch = useDispatch();
    const selectedWorkspaceItem = useSelector((state) => state.app.get("selectedItems"));
    const selectedValue = useSelector((state) => state.app.get("selectedSubtitles"));
    const selectedItem = selectedValue.size > 0 ? selectedValue?.get(0)?.split("-")[0] : selectedWorkspaceItem.get(0);
    const isMobile = useMobileDetection();
    const item = useSelector((state) =>
        state.projectDetails.getIn(["workspaceItems", selectedItem])
    );

    const project = useSelector((state) => state.projectDetails);
    const playhead = useSelector((state) => state.app.get("playhead"));
    const zoomData = item?.get("zoomData")?.toJS() || {};
    const projectWidth = project.get("width");
    const projectHeight = project.get("height");

    let pWidth = project.get("width");
    if (projectWidth < projectHeight && item && (item?.get("width") > item?.get("height"))) {
        pWidth = item.get("width");
    }
    if (project.get("width") === project.get("height")) {
        pWidth = project.get("width");
    }

    // ── Scale (tolerance) ────────────────────────────────────────
    const [tolerance, setTolerance] = useState(0);          // visible / remaining %
    const [toleranceInput, setToleranceInput] = useState("0");
    const [isInvalid, setIsInvalid] = useState(false);

    // ── Zoom Speed ───────────────────────────────────────────────
    const [zoomSpeed, setZoomSpeed] = useState("0.6");
    const [isZoomSpeedInvalid, setIsZoomSpeedInvalid] = useState(false);
    const [isZoomSpeedFocused, setIsZoomSpeedFocused] = useState(false);

    // ── Computed values ──────────────────────────────────────────

    const computedZoomSpeed = useMemo(() => {
        if (!item || !Object.keys(zoomData).length) return 0.6;

        const enterStart = item.get("enterStart") || 0;
        const videoStart = item.get("videoStart") || 0;
        const itemEnter = enterStart - videoStart;

        const key = Object.keys(zoomData).find((k) => {
            const group = zoomData[k];
            const zoomIn = Object.values(group).find((z) => z.type === "zoomIn");
            const zoomOut = Object.values(group).find((z) => z.type === "zoomOut");
            return zoomIn && zoomOut && playhead >= (zoomIn.start + itemEnter) && playhead <= (zoomOut.end + itemEnter);
        });

        if (!key) return 0.6;

        const group = zoomData[key];
        const zoomIn = Object.values(group).find((z) => z.type === "zoomIn");
        return Number(Math.max(0.1, zoomIn.end - zoomIn.start).toFixed(1));
    }, [zoomData, playhead, item]);

    const computedScalePercent = useMemo(() => {
        // After reset or no active group → default to 0% zoom (fully visible)
        if (!item || !Object.keys(zoomData).length) return 0;

        const enterStart = item.get("enterStart") || 0;
        const videoStart = item.get("videoStart") || 0;
        const itemEnter = enterStart - videoStart;

        const key = Object.keys(zoomData).find((k) => {
            const group = zoomData[k];
            const zoomIn = Object.values(group).find((z) => z.type === "zoomIn");
            const zoomOut = Object.values(group).find((z) => z.type === "zoomOut");
            return zoomIn && zoomOut && playhead >= (zoomIn.start + itemEnter) && playhead <= (zoomOut.end + itemEnter);
        });

        if (!key) return 0;

        const group = zoomData[key];
        const zoomIn = Object.values(group).find((z) => z.type === "zoomIn");
        if (!zoomIn) return 0;

        let scaleValue;
        if (projectHeight > projectWidth) {
            scaleValue = (zoomIn.height / project.get("height")) * 100;
        } else {
            scaleValue = (zoomIn.width / pWidth) * 100;
        }

        // Assuming your UI shows "how much is cropped/zoomed in" → higher % = more zoomed
        // If your slider shows remaining area, keep 100 - scaleValue
        return Math.round(100 - Math.max(scaleValue, 0));
    }, [zoomData, playhead, item, project, pWidth, projectHeight, projectWidth]);

    // ── Sync inputs after reset or playhead/group change ─────────
    useEffect(() => {
        // Scale
        const scaleVal = Math.round(computedScalePercent);
        setTolerance(scaleVal);
        setToleranceInput(scaleVal.toString());
        handleScaleValue(100 - scaleVal); // adjust if your handle expects different direction

        // Zoom speed (already mostly good, but reinforce)
        const speedVal = computedZoomSpeed.toFixed(1);
        if (!isZoomSpeedFocused) {
            setZoomSpeed(speedVal);
        }
    }, [computedScalePercent, computedZoomSpeed, handleScaleValue, isZoomSpeedFocused]);

    // ── Handlers ─────────────────────────────────────────────────


    const updateZoomInScale = (newPercent) => {
        if (!Object.keys(zoomData).length) return;

        const enterStart = item.get("enterStart") || 0;
        const videoStart = item.get("videoStart") || 0;
        const itemEnter = enterStart - videoStart;

        const key = Object.keys(zoomData).find((k) => {
            const g = zoomData[k];
            const zi = Object.values(g).find((z) => z.type === "zoomIn");
            const zo = Object.values(g).find((z) => z.type === "zoomOut");
            return zi && zo && playhead >= (zi.start + itemEnter) && playhead <= (zo.end + itemEnter);
        });

        if (!key) return;

        const updatedZoomData = { ...zoomData };
        const group = updatedZoomData[key];
        const zoomIn = Object.values(group).find((z) => z.type === "zoomIn");

        // Assuming your % means "zoomed in amount" → higher % = smaller width/height
        if (projectHeight > projectWidth) {
            zoomIn.height = Math.round((project.get("height") * (100 - newPercent)) / 100);
        } else {
            zoomIn.width = Math.round((pWidth * (100 - newPercent)) / 100);
        }

        const updatedProject = project.setIn(
            ["workspaceItems", item.get("id"), "zoomData"],
            fromJS(updatedZoomData)
        );

        const toUpdate = [{
            id: item.get("id"),
            container: "workspaceItems",
            toUpdate: { zoomData: updatedZoomData },
        }];

        dispatch(updateTimelineTime({ toUpdate }));
        const updatedLocalZoom = buildLocalSmartfocus(updatedProject);
        dispatch(updateProjectData({ localZoom: updatedLocalZoom.getIn(["localZoom"]) }));
    };

    const handleTolerance = (e) => {
        const val = e.target.value;
        if (val.length > 4) return;
        setToleranceInput(val);

        if (val === "") {
            setIsInvalid(false);
            return;
        }

        const num = parseInt(val, 10);
        if (num < 0 || num > 100) {
            setIsInvalid(true);
            return;
        }
        setIsInvalid(false);
        setTolerance(num);
        handleScaleValue(100 - num);

        // Optional: live update zoomData (like zoom speed)
        updateZoomInScale(num);
    };

    const handleSliderChange = (e, value) => {
        const val = Math.round(value);
        setTolerance(val);
        setToleranceInput(val.toString());
        handleScaleValue(100 - val);
        updateZoomInScale(val);
    };

    const getMaxZoomSpeed = () => {
        if (!Object.keys(zoomData).length) return 10;

        const enterStart = item.get("enterStart") || 0;
        const videoStart = item.get("videoStart") || 0;
        const itemEnter = enterStart - videoStart;

        const key = Object.keys(zoomData).find((k) => {
            const g = zoomData[k];
            const zi = Object.values(g).find((z) => z.type === "zoomIn");
            const zo = Object.values(g).find((z) => z.type === "zoomOut");
            return zi && zo && playhead >= (zi.start + itemEnter) && playhead <= (zo.end + itemEnter);
        });

        if (!key) return 10;

        const group = zoomData[key];
        const zoomIn = Object.values(group).find((z) => z.type === "zoomIn");
        const zoomOut = Object.values(group).find((z) => z.type === "zoomOut");
        if (!zoomIn || !zoomOut) return 10;

        return Math.min(((zoomOut.end - zoomIn.start) / 2).toFixed(1), 60);
    };

    const handleZoomSpeed = (e) => {
        const inputValue = e.target.value;
        if (inputValue.length > 4) return;
        if (!/^\d*\.?\d{0,1}$/.test(inputValue)) return;

        setZoomSpeed(inputValue);

        const value = parseFloat(inputValue);
        const max = parseFloat(getMaxZoomSpeed());

        if (value < 0.1 || value > max) {
            setIsZoomSpeedInvalid(true);
            return;
        }
        setIsZoomSpeedInvalid(false);

        if (!Object.keys(zoomData).length) return;

        const enterStart = item.get("enterStart") || 0;
        const videoStart = item.get("videoStart") || 0;
        const itemEnter = enterStart - videoStart;

        const key = Object.keys(zoomData).find((k) => {
            const g = zoomData[k];
            const zi = Object.values(g).find((z) => z.type === "zoomIn");
            const zo = Object.values(g).find((z) => z.type === "zoomOut");
            return zi && zo && playhead >= (zi.start + itemEnter) && playhead <= (zo.end + itemEnter);
        });

        if (!key) return;

        const updatedZoomData = { ...zoomData };
        const group = updatedZoomData[key];
        const zoomIn = Object.values(group).find((z) => z.type === "zoomIn");
        const zoomOut = Object.values(group).find((z) => z.type === "zoomOut");

        zoomIn.end = parseFloat((zoomIn.start + value).toFixed(2));
        zoomOut.start = parseFloat((zoomOut.end - value).toFixed(2));

        const updatedProject = project.setIn(
            ["workspaceItems", item.get("id"), "zoomData"],
            fromJS(updatedZoomData)
        );

        const toUpdate = [{
            id: item.get("id"),
            container: "workspaceItems",
            toUpdate: { zoomData: updatedZoomData },
        }];

        dispatch(updateTimelineTime({ toUpdate }));
        const updatedLocalZoom = buildLocalSmartfocus(updatedProject);
        dispatch(updateProjectData({ localZoom: updatedLocalZoom.getIn(["localZoom"]) }));
    };

    const handleZoomSpeedSliderChange = (e, value) => {
        const clamped = Math.max(0.1, Math.min(value, parseFloat(getMaxZoomSpeed())));
        setZoomSpeed(clamped.toFixed(1));
        handleZoomSpeed({ target: { value: clamped.toFixed(1) } });
    };

    const theme = useSelector((state) => state.app.get("theme"));

    return (
        <SfScaleContainer>
            <ScaleHeader>
                <Typography
                    content={"Scale"}
                    color={vmTheme[theme].panelPrimaryColor}
                    display="block"
                    font={font.mediumMini}
                />
                <SearchInputContainer style={{ pointerEvents: isLock ? 'none' : 'auto' }}>
                    <SearchInput
                        type="number"
                        value={toleranceInput}
                        placeholder="0%"
                        onKeyDownCapture={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        onKeyUpCapture={(e) => e.stopPropagation()}
                        onKeyUp={(e) => e.stopPropagation()}
                        onChange={handleTolerance}
                        onFocus={() => dispatch(setWorkspaceTextFocus(true))}
                        onBlur={() => dispatch(setWorkspaceTextFocus(false))}
                        style={{
                            width: toleranceInput.length > 3 ? '72px' : '62px',
                            borderColor: isInvalid ? '#ff4d4f' : undefined,
                            boxShadow: isInvalid ? '0 0 0 1px #ff4d4f' : undefined,
                            font: isMobile ? font.mediumMini_17 : font.normalMiniBase,
                        }}
                    />
                    {toleranceInput !== "" && (
                        <span
                            style={{
                                margin: toleranceInput.length > 3 ? "0 0 0 8px" : "0 0 0 3px",
                                position: "absolute",
                                left: isMobile ? "25px" : "27px",
                                top: isMobile ? "10px" : "11px",
                                color: vmTheme[theme].panelPrimaryColor,
                                font: isMobile ? font.mediumMini_17 : font.normalMiniBase,
                            }}
                        >
                            %
                        </span>
                    )}
                </SearchInputContainer>
            </ScaleHeader>

            <div className="slider" style={{ margin: "0 0 0 17px", pointerEvents: isLock ? 'none' : 'auto' }}>
                <Slider
                    min={0}
                    max={100}
                    value={tolerance}
                    step={1}
                    onChangeSliderValue={handleSliderChange}
                    onMouseUp={handleTolerance}
                    border="none"
                    borderRadius="3px"
                    background={vmTheme[theme].veryLightGray}
                    margin="0"
                    height="5px"
                    width="316px"
                    thumb={{
                        background: `${vmTheme[theme].polarColor} 0% 0% no-repeat padding-box`,
                        border: `2px solid ${vmTheme[theme].secondaryBorderColor}`,
                        height: "16px",
                        width: "16px",
                        hoverBG: `${vmTheme[theme].secondaryBorderColor}`,
                        hoverBorder: `1.7px solid ${vmTheme[theme].secondaryBorderColor}`,
                    }}
                    progressBackground={vmTheme[theme].secondaryBorderColor}
                    isChangeBg={true}
                />
            </div>

            <ScaleHeader>
                <Typography
                    content={"Zoom Speed"}
                    color={vmTheme[theme].panelPrimaryColor}
                    display="block"
                    font={font.mediumMini}
                />
                <SearchInputContainer style={{ pointerEvents: isLock ? 'none' : 'auto' }}>
                    <SearchInput
                        type="number"
                        value={isZoomSpeedFocused ? zoomSpeed : computedZoomSpeed.toFixed(1)}
                        onKeyDownCapture={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        onKeyUpCapture={(e) => e.stopPropagation()}
                        onKeyUp={(e) => e.stopPropagation()}
                        placeholder={`0.1-${getMaxZoomSpeed()}s`}
                        onChange={handleZoomSpeed}
                        onFocus={() => {
                            setIsZoomSpeedFocused(true);
                            setZoomSpeed(computedZoomSpeed.toFixed(1));
                            dispatch(setWorkspaceTextFocus(true));
                        }}
                        onBlur={() => {
                            setIsZoomSpeedFocused(false);
                            dispatch(setWorkspaceTextFocus(false));
                        }}
                        min="0.1"
                        max={getMaxZoomSpeed()}
                        step="0.1"
                        style={{
                            width: zoomSpeed.length > 3 ? '72px' : '62px',
                            borderColor: isZoomSpeedInvalid ? '#ff4d4f' : undefined,
                            boxShadow: isZoomSpeedInvalid ? '0 0 0 1px #ff4d4f' : undefined,
                            font: isMobile ? font.mediumMini_17 : font.normalMiniBase,
                        }}
                    />
                    {zoomSpeed !== "" && (
                        <span
                            style={{
                                margin: zoomSpeed.length > 3 ? "0 0 0 14px" : "0 0 0 10px",
                                position: "absolute",
                                left: isMobile ? "24px" : "22px",
                                top: isMobile ? "10px" : "11px",
                                color: vmTheme[theme].panelPrimaryColor,
                                font: isMobile ? font.mediumMini_17 : font.normalMiniBase,
                            }}
                        >
                            s
                        </span>
                    )}
                </SearchInputContainer>
            </ScaleHeader>

            <div className="slider" style={{ margin: "0 0 0 17px", pointerEvents: isLock ? 'none' : 'auto' }}>
                <Slider
                    min={0.1}
                    max={getMaxZoomSpeed()}
                    value={Number(isZoomSpeedFocused ? zoomSpeed : computedZoomSpeed)}
                    step={0.1}
                    onChangeSliderValue={handleZoomSpeedSliderChange}
                    onMouseUp={handleZoomSpeed}
                    border="none"
                    borderRadius="3px"
                    background={vmTheme[theme].veryLightGray}
                    margin="0"
                    height="5px"
                    width="316px"
                    thumb={{
                        background: `${vmTheme[theme].polarColor} 0% 0% no-repeat padding-box`,
                        border: `2px solid ${vmTheme[theme].secondaryBorderColor}`,
                        height: "16px",
                        width: "16px",
                        hoverBG: `${vmTheme[theme].secondaryBorderColor}`,
                        hoverBorder: `1.7px solid ${vmTheme[theme].secondaryBorderColor}`,
                    }}
                    progressBackground={vmTheme[theme].secondaryBorderColor}
                    isChangeBg={true}
                />
            </div>
        </SfScaleContainer>
    );
};

SmartfocusScale.propTypes = {
    handleScaleValue: PropTypes.func.isRequired,
    isLock: PropTypes.bool,
};

export default SmartfocusScale;